import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Landmark, CheckCircle2, Palette, Megaphone, BookOpen, Leaf, HeartPulse } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { useContent } from '../../context/ContentContext';

const gfwcAreas = [
  { icon: Palette, title: 'Arts & Culture' },
  { icon: Megaphone, title: 'Civic Engagement & Outreach' },
  { icon: BookOpen, title: 'Education & Libraries' },
  { icon: Leaf, title: 'Environment' },
  { icon: HeartPulse, title: 'Health & Wellness' },
];

const pathSteps = [
  {
    title: 'Align Our Programs',
    text: 'Our five program areas are built to align with GFWC community service programs from day one.',
    status: 'In progress',
  },
  {
    title: 'Complete Our Development Period',
    text: 'Recruit our 25 founding members, seat our founding board, and establish regular monthly meetings.',
    status: 'Fall 2026',
  },
  {
    title: 'Apply for Federation',
    text: 'After our initial development period, we plan to pursue membership in GFWC through the North Carolina federation.',
    status: 'Planned',
  },
];

const GFWCPage = () => {
  const { c } = useContent();
  return (
  <main className="pt-52" data-testid="gfwc-page">
    {/* Hero */}
    <section className="py-20 bg-[#f1e3eb]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <span className="text-[#bd8cae] text-sm uppercase tracking-wider font-medium">
            Our Bigger Picture
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#3c4a2a] mt-3 mb-6">
            {c('gfwc.hero.title', "The General Federation of Women's Clubs")}
          </h1>
          <p className="text-lg text-[#3c4a2a]/80 leading-relaxed">
            {c('gfwc.hero.text', "The Women's Club of Lincoln County is being built in the tradition of the General Federation of Women's Clubs (GFWC) — with the goal of joining the Federation after our initial development period.")}
          </p>
        </div>
      </div>
    </section>

    {/* What is GFWC */}
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="w-14 h-14 rounded-full bg-[#bd8cae]/40 flex items-center justify-center mb-6">
              <Landmark className="w-7 h-7 text-[#5d6b3c]" />
            </div>
            <h2 className="font-serif text-3xl text-[#3c4a2a] mb-6">What is GFWC?</h2>
            <div className="space-y-4 text-[#3c4a2a]/80 leading-relaxed">
              <p>{c('gfwc.what.p1', "Founded in 1890, the General Federation of Women's Clubs is one of the world's largest and oldest women's volunteer service organizations. Headquartered in Washington, D.C., GFWC unites local women's clubs across the United States and around the world.")}</p>
              <p>{c('gfwc.what.p2', 'GFWC clubs are nonpartisan and community-focused — dedicated to improving their communities through volunteer service, from supporting education and the arts to advancing health, the environment, and civic life.')}</p>
              <p>{c('gfwc.what.p3', 'Joining the Federation connects a local club to a national network of resources, training, and sisterhood — while every club remains rooted in the needs of its own community.')}</p>
            </div>
          </div>
          <div>
            <Card className="bg-[#f1e3eb] border-0">
              <CardContent className="p-8">
                <h3 className="font-serif text-xl text-[#3c4a2a] mb-6">
                  GFWC Community Service Program Areas
                </h3>
                <ul className="space-y-4">
                  {gfwcAreas.map((area) => (
                    <li key={area.title} className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-[#5d6b3c]/15 flex items-center justify-center mr-4 flex-shrink-0">
                        <area.icon className="w-5 h-5 text-[#5d6b3c]" />
                      </div>
                      <span className="text-[#3c4a2a] font-medium">{area.title}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-[#3c4a2a]/60 mt-6">
                  Our five WCLC program areas are designed to align with these — so we're 
                  Federation-ready from the start.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>

    {/* Our path */}
    <section className="py-20 bg-[#f1e3eb]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-[#bd8cae] text-sm uppercase tracking-wider font-medium">
            The Road to Federation
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl text-[#3c4a2a] mt-3">
            Our Path to Joining GFWC
          </h2>
        </div>
        <div className="space-y-6">
          {pathSteps.map((step, i) => (
            <Card key={step.title} className="bg-white border-0 shadow-md" data-testid={`gfwc-path-step-${i + 1}`}>
              <CardContent className="p-6 sm:p-8 flex items-start gap-6">
                <div className="w-12 h-12 rounded-full bg-[#5d6b3c] text-white font-serif text-xl flex items-center justify-center flex-shrink-0">
                  {i + 1}
                </div>
                <div className="flex-1">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                    <h3 className="font-serif text-xl text-[#3c4a2a]">{step.title}</h3>
                    <span className="inline-flex items-center text-xs font-medium uppercase tracking-wider text-[#5d6b3c] bg-[#5d6b3c]/10 px-3 py-1 rounded-full w-fit">
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                      {step.status}
                    </span>
                  </div>
                  <p className="text-[#3c4a2a]/70">{step.text}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        <p className="text-center text-sm text-[#3c4a2a]/60 mt-10">
          Learn more about the Federation at{' '}
          <a
            href="https://www.gfwc.org"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#5d6b3c] underline hover:text-[#3c4a2a]"
            data-testid="gfwc-external-link"
          >
            gfwc.org
          </a>
        </p>
      </div>
    </section>

    {/* CTA */}
    <section className="py-20 bg-[#5d6b3c]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="font-serif text-3xl sm:text-4xl text-white mb-6">
          Help Us Get There
        </h2>
        <p className="text-white/90 text-lg mb-8">
          Our founding members will guide every step of this journey. Be one of the 25.
        </p>
        <Link to="/founding-members">
          <Button
            data-testid="gfwc-founding-cta"
            className="bg-[#bd8cae] hover:bg-[#bd8cae]/90 text-[#3c4a2a] px-8 py-6 text-lg"
          >
            Become a Founding Member
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </Link>
      </div>
    </section>
  </main>
  );
};

export default GFWCPage;
